import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Sparkles, 
  Users, 
  Target, 
  Calendar, 
  ArrowRight, 
  Star,
  Building2,
  MapPin,
  Clock,
  BookOpen,
  TrendingUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useIntelligentMatchmaking } from '@/hooks/useIntelligentMatchmaking';
import { useOpportunities } from '@/hooks/useOpportunities';
import { useEvents } from '@/hooks/useEvents';
import { useInsights } from '@/hooks/useInsights';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { UpgradePrompt } from '@/components/ui/upgrade-prompt';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const SmartRecommendations = () => {
  const navigate = useNavigate();
  const { member } = useAuth();
  const { subscription } = useSubscription();
  const { suggestions, isLoading } = useIntelligentMatchmaking({ limit: 5 });
  const { opportunities } = useOpportunities();
  const { events, registeredEvents, handleEventRegistration } = useEvents();
  const { insights } = useInsights();

  // Get top recommendations - now using the new structure
  const topConnections = suggestions.slice(0, 3);
  
  // Filter opportunities by user's city/country
  const userLocation = member?.city || member?.country;
  const filteredOpportunities = opportunities?.filter(opp => {
    if (!userLocation) return true;
    return opp.location?.toLowerCase().includes(userLocation.toLowerCase()) ||
           opp.company?.toLowerCase().includes(userLocation.toLowerCase());
  }) || [];
  const relevantOpportunities = filteredOpportunities.length > 0 
    ? filteredOpportunities.slice(0, 3) 
    : opportunities?.slice(0, 3) || [];

  // Prioriser les événements First Tuesday (toujours visibles)
  const firstTuesdayEvents = events?.filter(event => 
    event.title?.toLowerCase().includes('first tuesday') &&
    new Date(event.start_date) > new Date()
  ) || [];

  // Filtrer les autres événements par ville
  const localEvents = events?.filter(event => {
    if (!member?.city) return true;
    if (event.title?.toLowerCase().includes('first tuesday')) return false; // Éviter doublons
    return event.location?.toLowerCase().includes(member.city.toLowerCase());
  }) || [];

  // Combiner : First Tuesday en priorité, puis événements locaux
  const filteredEvents = [...firstTuesdayEvents, ...localEvents];

  const upcomingEvents = filteredEvents
    .filter(event => new Date(event.start_date) > new Date())
    .slice(0, 3);

  // Filter insights for user interests and get recent ones
  const recentInsights = insights
    ?.filter(insight => insight.status === 'published')
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 3) || [];

  return (
    <Card className="h-fit">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Recommandations Intelligentes
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {userLocation ? `Personnalisées pour ${userLocation}` : 'Basées sur votre profil et activités'}
        </p>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="connections" className="w-full">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 gap-1">
            <TabsTrigger value="connections" className="text-[10px] sm:text-xs">
              <Users className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
              <span className="hidden sm:inline ml-1">Connexions</span>
            </TabsTrigger>
            <TabsTrigger value="opportunities" className="text-[10px] sm:text-xs">
              <Target className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
              <span className="hidden sm:inline ml-1">Opportunités</span>
            </TabsTrigger>
            <TabsTrigger value="events" className="text-[10px] sm:text-xs">
              <Calendar className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
              <span className="hidden sm:inline ml-1">Événements</span>
            </TabsTrigger>
            <TabsTrigger value="insights" className="text-[10px] sm:text-xs">
              <BookOpen className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
              <span className="hidden sm:inline ml-1">Insights</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="connections" className="space-y-4 mt-4">
            <div className="space-y-3">
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  <div className="animate-pulse">Chargement...</div>
                </div>
              ) : topConnections.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucune suggestion pour le moment</p>
                </div>
              ) : (
                topConnections.map((suggestion) => (
                  <div key={suggestion.member.id} className="flex items-center justify-between p-2 sm:p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={suggestion.member.image || ''} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {`${suggestion.member.firstname?.[0] || ''}${suggestion.member.lastname?.[0] || ''}`.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">
                          {suggestion.member.firstname} {suggestion.member.lastname}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {suggestion.member.role} {suggestion.member.company && `• ${suggestion.member.company}`}
                        </p>
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                          <span className="text-xs text-muted-foreground">
                            {Math.round(suggestion.compatibility)}% - {suggestion.categories?.[0] || 'Profils compatibles'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="text-xs">
                      Connecter
                    </Button>
                  </div>
                ))
              )}
            </div>
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => navigate('/networking')}
            >
              Voir tout le réseau
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </TabsContent>

          <TabsContent value="opportunities" className="space-y-4 mt-4">
            <div className="space-y-3">
              {relevantOpportunities.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {userLocation ? `Aucune opportunité trouvée pour ${userLocation}` : 'Aucune opportunité recommandée'}
                  </p>
                </div>
              ) : (
                relevantOpportunities.map((opportunity) => (
                  <div key={opportunity.id} className="p-2 sm:p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-sm line-clamp-1">{opportunity.title}</h4>
                      <Badge variant="outline" className="text-xs">{opportunity.type}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                      <div className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {opportunity.company}
                      </div>
                      {opportunity.location && (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {opportunity.location}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                      {opportunity.description}
                    </p>
                    <Button size="sm" variant="outline" className="text-xs">
                      Voir l'opportunité
                    </Button>
                  </div>
                ))
              )}
            </div>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/opportunities')}
            >
              Toutes les opportunités
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </TabsContent>

          <TabsContent value="events" className="space-y-4 mt-4">
            <div className="space-y-3">
              {upcomingEvents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {member?.city ? `Aucun événement trouvé à ${member.city}` : 'Aucun événement à venir'}
                  </p>
                </div>
              ) : (
                upcomingEvents.map((event) => (
                  <div key={event.id} className="p-2 sm:p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-sm line-clamp-1">{event.title}</h4>
                      {event.is_premium && <Badge variant="secondary" className="text-xs">Premium</Badge>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(event.start_date).toLocaleDateString('fr-FR')}
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {event.location}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                      {event.description}
                    </p>
                    <Button 
                      size="sm" 
                      variant={registeredEvents.includes(event.id) ? "outline" : "default"}
                      className="text-xs"
                      onClick={() => handleEventRegistration(event.id)}
                      disabled={registeredEvents.includes(event.id)}
                    >
                      {registeredEvents.includes(event.id) ? "✓ Déjà inscrit" : "S'inscrire"}
                    </Button>
                  </div>
                ))
              )}
            </div>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/events')}
            >
              Tous les événements
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </TabsContent>

          <TabsContent value="insights" className="space-y-4 mt-4">
            <div className="space-y-3">
              {recentInsights.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucun insight disponible pour le moment</p>
                </div>
              ) : (
                recentInsights.map((insight) => (
                  <div key={insight.id} className="p-2 sm:p-3 border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                       onClick={() => navigate(`/insights/${insight.id}`)}>
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-sm line-clamp-2 flex-1 mr-2">{insight.title}</h4>
                      <Badge variant="outline" className="text-xs shrink-0">{insight.category}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                      <div className="flex items-center gap-1">
                        <TrendingUp className="h-3 w-3" />
                        {insight.author_name || insight.source_name || 'Hub Leaders'}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {insight.reading_time_minutes || 3} min
                      </div>
                    </div>
                    {insight.summary && (
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                        {insight.summary}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-muted-foreground">
                        {new Date(insight.created_at).toLocaleDateString('fr-FR')}
                      </div>
                      {insight.is_featured && (
                        <Badge variant="secondary" className="text-xs">
                          <Star className="h-3 w-3 mr-1" />
                          Featured
                        </Badge>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/insights')}
            >
              Tous les insights
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default SmartRecommendations;